import { useCallback, useEffect, useRef, useState } from 'react';
import gql from 'graphql-tag';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { cfetch } from '@confluence/network';

import type {
	RawAction,
	RawSuggestion,
	SpaceSuggestionsResponse,
} from '../__types__/SpaceSuggestion.ts';
import { parseSuggestionTitle } from '../utils/parseSuggestionTitle.ts';

export type UseFetchSpaceSuggestionsResult = {
	suggestions: RawSuggestion[];
	isLoading: boolean;
	error: Error | undefined;
	refetch: () => Promise<void>;
};

const buildSpaceSuggestionsUrl = (spaceKey: string): string =>
	`/wiki/rest/internal/1.0/spaces/${spaceKey}/organization/suggestions`;

type ContentTitleNode = {
	id: string | null | undefined;
	title: string | null | undefined;
};

type ContentTitleConnection = {
	nodes?: readonly (ContentTitleNode | null)[] | null;
} | null;

/**
 * A content reference resolved to its human-readable title and (best-effort)
 * content type. `contentType` uses the lowercase vocabulary shared by the AGG
 * content lookup and `titleReferences` (e.g. `page`, `database`, `folder`).
 */
type ResolvedContent = {
	title: string;
	contentType?: string;
};

type ContentDetailsById = Record<string, ResolvedContent>;

type ContentTitlesQueryData = {
	pageContent?: ContentTitleConnection;
	folderContent?: ContentTitleConnection;
	whiteboardContent?: ContentTitleConnection;
	databaseContent?: ContentTitleConnection;
	embedContent?: ContentTitleConnection;
	blogpostContent?: ContentTitleConnection;
	slideContent?: ContentTitleConnection;
};

type ContentTitlesQueryVariables = {
	ids: string[];
};

const ContentTitlesQuery = gql`
	query useFetchSpaceSuggestionsContentTitlesQuery($ids: [ID!]!) {
		pageContent: content(ids: $ids, type: "page") {
			nodes {
				id
				title
			}
		}
		folderContent: content(ids: $ids, type: "folder") {
			nodes {
				id
				title
			}
		}
		whiteboardContent: content(ids: $ids, type: "whiteboard") {
			nodes {
				id
				title
			}
		}
		databaseContent: content(ids: $ids, type: "database") {
			nodes {
				id
				title
			}
		}
		embedContent: content(ids: $ids, type: "embed") {
			nodes {
				id
				title
			}
		}
		blogpostContent: content(ids: $ids, type: "blogpost") {
			nodes {
				id
				title
			}
		}
		slideContent: content(ids: $ids, type: "slide") {
			nodes {
				id
				title
			}
		}
	}
`;

/**
 * Extracts the content ID referenced by a single action, or `undefined` if
 * the action type does not reference content.
 */
function getContentIdFromAction(action: RawAction): string | undefined {
	switch (action.actionType) {
		case 'MOVE':
		case 'RENAME':
			return action.parameters.contentId;
		case 'CREATE_FOLDER':
			return action.parameters.parentContentId;
		default:
			return undefined;
	}
}

/**
 * Extracts every content ID referenced by the actions in a set of suggestions,
 * skipping suggestions that already carry `titleReferences` (those names are
 * resolved locally without a network call).
 * Returns a deduplicated array of IDs.
 */
function* iterateSuggestionActions(
	suggestions: RawSuggestion[],
): Generator<RawSuggestion['actions'][number]> {
	for (const suggestion of suggestions) {
		if (!suggestion.titleReferences) {
			yield* suggestion.actions;
		}
	}
}

function collectContentIds(suggestions: RawSuggestion[]): string[] {
	const ids = new Set<string>();
	for (const action of iterateSuggestionActions(suggestions)) {
		const id = getContentIdFromAction(action);
		if (id !== undefined) {
			ids.add(id);
		}
	}
	return Array.from(ids);
}

/**
 * Batch-fetches content titles for the given IDs using Confluence AGG content
 * lookup. Returns a map from content ID to its resolved title and content type.
 * The content type is derived from which typed query bucket the node resolved
 * in (e.g. a node in `databaseContent` is a `database`).
 *
 * IDs that cannot be resolved (e.g. deleted content, aliases, content types
 * outside the queried set) are silently omitted from the returned map —
 * callers should fall back to the raw ID.
 */
async function fetchContentDetails(contentIds: string[]): Promise<ContentDetailsById> {
	if (contentIds.length === 0) {
		return {};
	}

	try {
		const { data, error } = await queryInterop<ContentTitlesQueryData, ContentTitlesQueryVariables>(
			{
				query: ContentTitlesQuery as unknown as Parameters<typeof queryInterop>[0]['query'],
				variables: {
					ids: contentIds,
				},
				fetchPolicy: 'network-only',
				interopApolloAggClient: true,
			},
		);

		if (error) {
			return {};
		}

		// Each connection maps to a known content type; collect every resolved
		// node tagged with the type of the bucket it came from so callers can
		// pick a matching icon. Buckets are collected with separate single-level
		// loops (not nested iteration) to keep resolution linear.
		const details: ContentDetailsById = {};
		collectContentDetails(details, data?.pageContent, 'page');
		collectContentDetails(details, data?.folderContent, 'folder');
		collectContentDetails(details, data?.whiteboardContent, 'whiteboard');
		collectContentDetails(details, data?.databaseContent, 'database');
		collectContentDetails(details, data?.embedContent, 'embed');
		collectContentDetails(details, data?.blogpostContent, 'blogpost');
		collectContentDetails(details, data?.slideContent, 'slide');
		return details;
	} catch {
		// Content resolution is best-effort; fall back to raw IDs on failure.
		return {};
	}
}

/**
 * Adds every resolvable node from a single typed content connection into
 * `details`, tagging each with the connection's `contentType`.
 */
function collectContentDetails(
	details: ContentDetailsById,
	connection: ContentTitleConnection | undefined,
	contentType: string,
): void {
	for (const node of connection?.nodes ?? []) {
		if (node?.id && node.title) {
			details[node.id] = { title: node.title, contentType };
		}
	}
}

/**
 * Builds a map from CREATE_FOLDER alias → folder title within a single
 * suggestion. Used to resolve `destinationAlias` in MOVE actions to a
 * human-readable folder name.
 */
function buildAliasToTitleMap(actions: RawAction[]): Record<string, string> {
	const map: Record<string, string> = {};
	for (const action of actions) {
		if (action.actionType === 'CREATE_FOLDER') {
			map[action.parameters.alias] = action.parameters.title;
		}
	}
	return map;
}

/**
 * Returns a copy of the action with any referenced content ID replaced by its
 * resolved human-readable name and content type from `details`, and any
 * destination alias replaced by the folder title from `aliasToTitle`. Falls
 * back to the raw ID or alias when a name is not available; `contentType` is
 * left undefined when unresolved so the renderer can pick a fallback icon.
 */
function enrichActionWithDetails(
	action: RawAction,
	details: ContentDetailsById,
	aliasToTitle: Record<string, string>,
): RawAction {
	switch (action.actionType) {
		case 'MOVE': {
			const resolved = details[action.parameters.contentId];
			return {
				...action,
				parameters: {
					...action.parameters,
					contentName: resolved?.title ?? action.parameters.contentId,
					contentType: resolved?.contentType,
					destinationName:
						aliasToTitle[action.parameters.destinationAlias] ?? action.parameters.destinationAlias,
				},
			};
		}
		case 'RENAME': {
			const resolved = details[action.parameters.contentId];
			return {
				...action,
				parameters: {
					...action.parameters,
					contentName: resolved?.title ?? action.parameters.contentId,
					contentType: resolved?.contentType,
				},
			};
		}
		case 'CREATE_FOLDER':
			return {
				...action,
				parameters: {
					...action.parameters,
					parentContentName:
						details[action.parameters.parentContentId]?.title ?? action.parameters.parentContentId,
				},
			};
		default:
			return action;
	}
}

/**
 * Enriches raw suggestion actions with resolved content names, content types,
 * and alias titles. Falls back to the raw content ID or alias when a value is
 * not available.
 */
function enrichActionsWithDetails(actions: RawAction[], details: ContentDetailsById): RawAction[] {
	const aliasToTitle = buildAliasToTitleMap(actions);
	return actions.map((action) => enrichActionWithDetails(action, details, aliasToTitle));
}

/**
 * Builds the content-details map used to enrich a single suggestion's actions,
 * keyed by content ID. Sources are layered lowest-to-highest priority:
 *
 * 1. `aggDetails` — globally-fetched AGG titles/types (fallback).
 * 2. Content references parsed from the suggestion title — the same source that
 *    drives the card summary icons (via `parseSuggestionTitle`), covering both
 *    inline object literals and bare `{id}` refs resolved through
 *    `titleReferences`. This keeps the review rows consistent with the cards
 *    even when AGG cannot resolve the content IDs.
 * 3. Structured `titleReferences` whose IDs may not appear literally in the
 *    title text.
 */
function buildSuggestionDetails(
	suggestion: RawSuggestion,
	aggDetails: ContentDetailsById,
): ContentDetailsById {
	const details: ContentDetailsById = { ...aggDetails };

	for (const segment of parseSuggestionTitle(suggestion.title, suggestion.titleReferences)) {
		if (segment.type === 'link') {
			details[segment.contentId] = { title: segment.title, contentType: segment.contentType };
		}
	}

	if (suggestion.titleReferences) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (const id in suggestion.titleReferences) {
			const ref = suggestion.titleReferences[id];
			details[ref.contentId] = { title: ref.title, contentType: ref.contentType || undefined };
		}
	}

	return details;
}

function enrichSuggestionsWithDetails(
	suggestions: RawSuggestion[],
	aggDetails: ContentDetailsById,
): RawSuggestion[] {
	return suggestions.map((suggestion) => ({
		...suggestion,
		actions: enrichActionsWithDetails(
			suggestion.actions,
			buildSuggestionDetails(suggestion, aggDetails),
		),
	}));
}

/**
 * Fetches the Space Organizer Inbox suggestions for the given space.
 *
 * Note: the backend endpoint is currently a mock and returns hardcoded data
 * regardless of the `spaceKey` provided. Migrate to a GraphQL query (via
 * `@confluence/apollo-relay-interop`) once the AGG schema is ready — REST is
 * deprecated for new Confluence frontend data fetching.
 *
 * After fetching suggestions, content IDs referenced by actions are
 * batch-resolved to human-readable titles via AGG content lookup.
 *
 * Concurrency: tracks an epoch per call so that a stale response from a
 * previous `spaceKey` cannot overwrite state for the current `spaceKey`.
 */
export const useFetchSpaceSuggestions = (spaceKey: string): UseFetchSpaceSuggestionsResult => {
	const [suggestions, setSuggestions] = useState<RawSuggestion[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | undefined>(undefined);
	const callEpochRef = useRef<number>(0);

	const refetch = useCallback(async () => {
		const epoch = callEpochRef.current + 1;
		callEpochRef.current = epoch;

		setIsLoading(true);
		setError(undefined);

		try {
			const response = await cfetch(buildSpaceSuggestionsUrl(spaceKey), {
				method: 'GET',
			});

			if (!response.ok) {
				throw new Error(
					`Failed to fetch space suggestions: ${response.status} ${response.statusText}`,
				);
			}

			const data: SpaceSuggestionsResponse = await response.json();

			// Discard the response if a newer call has been issued in the meantime.
			if (callEpochRef.current !== epoch) {
				return;
			}

			const rawSuggestions = data.suggestions ?? [];

			// Resolve content IDs to human-readable titles and types (best-effort).
			const contentIds = collectContentIds(rawSuggestions);
			const contentDetails = await fetchContentDetails(contentIds);

			// Re-check epoch after the async content resolution.
			if (callEpochRef.current !== epoch) {
				return;
			}

			setSuggestions(enrichSuggestionsWithDetails(rawSuggestions, contentDetails));
		} catch (err) {
			if (callEpochRef.current !== epoch) {
				return;
			}
			setError(err instanceof Error ? err : new Error(String(err)));
			setSuggestions([]);
		} finally {
			if (callEpochRef.current === epoch) {
				setIsLoading(false);
			}
		}
	}, [spaceKey]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void refetch();
	}, [refetch]);

	return { suggestions, isLoading, error, refetch };
};
